/**
 * TermsGate.tsx — the Terms of Use acceptance gate (REQUIREMENTS_TERMS_OF_USE.md) and the footer
 * "Terms of Use" link that opens the same policy for reading at any time (TU-F08).
 *
 * THE GATE (TU-F02..F07): a signed-in person who has not accepted the CURRENT policy sees this in
 * place of the routed page (App.tsx decides; the frame stays). It:
 *   - fetches and renders the live policy (GET /api/me/terms/current), with its version and effective
 *     date (TU-F09), and — when this is a re-gate after a material change — the plain-words summary of
 *     what changed at the very top (TU-F12);
 *   - requires the person to reach the END of the policy before Accept enables (TU-F02/F03). The
 *     disabled Accept says WHY, and an aria-live region announces when it unlocks, so the reason is
 *     available to a screen-reader user, not just conveyed by a greyed-out button (TU-F07/NFR01);
 *   - is fully keyboard-operable (TU-F07 / PC-NFR01 WCAG 2.1 AA): focus moves to the gate heading when
 *     it appears (so it is never lost on the replaced route), the scroll region is a focusable tab stop
 *     that keyboard scrolling advances toward "reached the end", every control has a visible focus
 *     ring, and the footer dialog traps Tab within itself and restores focus to its opener on close;
 *   - on Accept, POSTs to /api/me/terms/accept and re-probes auth (TU-F04) so the app appears in place;
 *   - on Decline, shows an "acceptance required" screen with the policy STILL readable and an obvious
 *     way back to reconsider (TU-F05) — it records nothing and locks nobody out permanently.
 *
 * NO SCROLL TELEMETRY IS SENT (TU-NFR06): "scrolled to the end" is a local UI fact that only enables a
 * button. The one thing that ever reaches the server is the acceptance itself.
 *
 * STYLING reuses the frame's --tb-* tokens (frame.css) so the gate reads as part of the product, not a
 * bolted-on modal. No external assets (CSP): the styles are a scoped block below.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';

/** The policy shape returned by GET /api/me/terms/current. Mirrors db/termsOfUse.ts CurrentPolicy. */
interface CurrentPolicy {
  versionId: string;
  versionLabel: string;
  body: string;
  effectiveDate: string;
  changeKind: 'material' | 'minor';
  changeSummary: string | null;
}

// A pixel slack so "scrolled to the end" fires even when sub-pixel layout or a trackpad leaves a
// hair of scroll range unreached. Reaching within this many px of the bottom counts as the end.
const SCROLL_END_SLACK_PX = 24;

/**
 * Render the plain-text policy body. The body uses a tiny, fixed markup (authored in migration 0033):
 * `# ` / `## ` headings, `**bold**` inline, blank-line-separated paragraphs. We render it with real
 * elements rather than pulling in a markdown library — the vocabulary is small and fixed, and no
 * external/eval'd code touches the CSP-locked page. Unknown lines render as plain paragraphs.
 */
function renderPolicyBody(body: string): React.ReactNode {
  const lines = body.replace(/\r\n/g, '\n').split('\n');
  const blocks: React.ReactNode[] = [];
  let para: string[] = [];

  const flushPara = () => {
    if (para.length === 0) return;
    blocks.push(<p key={`p${blocks.length}`}>{renderInline(para.join(' '))}</p>);
    para = [];
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    if (line.trim() === '') {
      flushPara();
    } else if (line.startsWith('## ')) {
      flushPara();
      blocks.push(<h3 key={`h${blocks.length}`}>{renderInline(line.slice(3))}</h3>);
    } else if (line.startsWith('# ')) {
      flushPara();
      blocks.push(<h2 key={`h${blocks.length}`}>{renderInline(line.slice(2))}</h2>);
    } else {
      para.push(line.trim());
    }
  }
  flushPara();
  return blocks;
}

/** Render `**bold**` runs within a line as <strong>; everything else is plain text. */
function renderInline(text: string): React.ReactNode {
  const parts = text.split(/(\*\*[^*]+\*\*)/g).filter((s) => s.length > 0);
  return parts.map((part, i) =>
    part.startsWith('**') && part.endsWith('**') ? (
      <strong key={i}>{part.slice(2, -2)}</strong>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'none' } // no published policy — App.tsx won't mount the gate, but be defensive
  | { status: 'ready'; policy: CurrentPolicy };

/**
 * The reusable policy reader: fetches the current policy and renders it in a scrollable region. The
 * caller supplies what appears BELOW the scroll region (the gate's accept/decline controls, or the
 * footer dialog's close button) and is told, via onReachedEnd, when the person has scrolled to the
 * bottom. Used by both the gate and the footer dialog so the policy renders identically in each.
 */
function PolicyReader({
  policy,
  onReachedEnd,
  scrollLabelledBy,
}: {
  policy: CurrentPolicy;
  onReachedEnd?: () => void;
  scrollLabelledBy: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const checkEnd = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atEnd = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_END_SLACK_PX;
    if (atEnd) onReachedEnd?.();
  }, [onReachedEnd]);

  // If the policy is short enough that there is nothing to scroll, the person has already "reached the
  // end" — enable acceptance immediately rather than demanding an impossible scroll (TU-F03 edge).
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (el.scrollHeight - el.clientHeight <= SCROLL_END_SLACK_PX) onReachedEnd?.();
  }, [onReachedEnd]);

  return (
    <div
      className="tb-terms__scroll"
      role="region"
      aria-labelledby={scrollLabelledBy}
      // tabIndex={0} on this scrollable region is DELIBERATE and WCAG-correct: a keyboard-only user
      // must be able to focus the policy pane to scroll it with the arrow keys / Page Down
      // (WCAG 2.1.1), and reaching the end is what unlocks acceptance (TU-F03).
      // no-noninteractive-tabindex does not account for scroll containers.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
      ref={scrollRef}
      onScroll={onReachedEnd ? checkEnd : undefined}
    >
      {policy.changeSummary ? (
        // TU-F12: a returning person re-gated by a material change sees WHAT changed, first.
        <div className="tb-terms__whatchanged" role="note">
          <strong>What changed in this version:</strong> {policy.changeSummary}
        </div>
      ) : null}
      <div className="tb-terms__body">{renderPolicyBody(policy.body)}</div>
    </div>
  );
}

/** Fetch the current policy once. Shared by the gate and the footer dialog. */
function useCurrentPolicy(): LoadState {
  const [state, setState] = useState<LoadState>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    fetch('/api/me/terms/current', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 204) {
          setState({ status: 'none' });
          return;
        }
        if (!res.ok) {
          setState({ status: 'error' });
          return;
        }
        const policy = (await res.json()) as CurrentPolicy;
        if (!cancelled) setState({ status: 'ready', policy });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
}

/**
 * The gate itself. Rendered by App.tsx in place of the routed page when the signed-in person has not
 * accepted the current policy. `onAccepted` re-probes /api/me so the app appears in place.
 */
export function TermsGate({ onAccepted }: { onAccepted: () => void }) {
  const load = useCurrentPolicy();
  const [reachedEnd, setReachedEnd] = useState(false);
  const [declined, setDeclined] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const headingId = useId();
  const liveId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  // TU-F07 / PC-NFR01: when the gate appears (and each time we switch between the accept and decline
  // screens), move focus to its heading. Without this, a keyboard or screen-reader user's focus stays
  // on the now-replaced route; they would have no idea a blocking gate had taken over the frame. The
  // heading is made programmatically focusable (tabIndex -1) purely as a focus target, not a tab stop.
  useEffect(() => {
    if (load.status === 'ready') headingRef.current?.focus();
  }, [load.status, declined]);

  const markEnd = useCallback(() => setReachedEnd(true), []);

  const accept = useCallback(() => {
    if (load.status !== 'ready' || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    fetch('/api/me/terms/accept', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ versionId: load.policy.versionId }),
    })
      .then((res) => {
        if (res.status === 204) {
          onAccepted(); // re-probe: mustAcceptTerms flips false and the app appears
          return;
        }
        if (res.status === 409) {
          // The policy changed under us between render and click. Reload so the person is shown the
          // now-current policy to accept, rather than silently accepting something they didn't read.
          setSubmitError('The policy was updated. Reloading the latest version…');
          window.location.reload();
          return;
        }
        setSubmitError('Something went wrong recording your acceptance. Please try again.');
        setSubmitting(false);
      })
      .catch(() => {
        setSubmitError('Network problem recording your acceptance. Please try again.');
        setSubmitting(false);
      });
  }, [load, submitting, onAccepted]);

  if (load.status === 'loading') {
    return (
      <div className="tb-terms" aria-busy="true">
        <p className="tb-terms__status">Loading the Terms of Use…</p>
      </div>
    );
  }
  if (load.status === 'error') {
    return (
      <div className="tb-terms" role="alert">
        <p className="tb-terms__status">
          The Terms of Use could not be loaded. Please refresh the page to try again.
        </p>
      </div>
    );
  }
  // Defensive: App.tsx does not mount the gate when there is no published policy, so if we somehow
  // reach here with none, do not trap the person — treat it as nothing to accept.
  if (load.status === 'none') {
    onAccepted();
    return null;
  }

  const { policy } = load;

  // The DECLINE screen (TU-F05): acceptance is required to use the platform, said plainly, with the
  // policy STILL readable and an obvious way back. Nothing was recorded; this is not a lock-out.
  if (declined) {
    return (
      <div className="tb-terms" role="dialog" aria-modal="false" aria-labelledby={headingId}>
        <div className="tb-terms__panel">
          <h1 id={headingId} className="tb-terms__title" tabIndex={-1} ref={headingRef}>
            Accepting the Terms of Use is required
          </h1>
          <p className="tb-terms__lede">
            You can keep reading the Terms below. To use Trailblazer you need to accept them —
            nothing has been recorded, and you can go back and accept whenever you are ready.
          </p>
          <PolicyReader policy={policy} scrollLabelledBy={headingId} />
          <div className="tb-terms__actions">
            <button type="button" className="tb-terms__accept" onClick={() => setDeclined(false)}>
              Back to the Terms
            </button>
          </div>
        </div>
      </div>
    );
  }

  // The ACCEPT gate (TU-F02/F03/F09).
  return (
    <div className="tb-terms" role="dialog" aria-modal="false" aria-labelledby={headingId}>
      <div className="tb-terms__panel">
        <h1 id={headingId} className="tb-terms__title" tabIndex={-1} ref={headingRef}>
          Terms of Use
        </h1>
        <p className="tb-terms__meta">
          Version {policy.versionLabel} · Effective {formatDate(policy.effectiveDate)}
        </p>
        <p className="tb-terms__lede">
          Please read these Terms in full — it takes about a minute. Scroll to the end, then accept
          to continue.
        </p>

        <PolicyReader policy={policy} onReachedEnd={markEnd} scrollLabelledBy={headingId} />

        {/* aria-live: announces to a screen reader the moment Accept unlocks, so the state change is
            not conveyed by the greyed-out button alone (TU-F07 / PC-NFR01 WCAG AA). */}
        <p id={liveId} className="tb-terms__hint" aria-live="polite">
          {reachedEnd
            ? 'You have reached the end. You can now accept.'
            : 'Scroll to the end of the Terms to enable Accept.'}
        </p>

        {submitError ? (
          <p className="tb-terms__error" role="alert">
            {submitError}
          </p>
        ) : null}

        <div className="tb-terms__actions">
          <button
            type="button"
            className="tb-terms__accept"
            onClick={accept}
            disabled={!reachedEnd || submitting}
            aria-describedby={liveId}
          >
            {submitting ? 'Accepting…' : 'Accept and continue'}
          </button>
          <button
            type="button"
            className="tb-terms__decline"
            onClick={() => setDeclined(true)}
            disabled={submitting}
          >
            Decline
          </button>
        </div>
      </div>
      <TermsGateStyles />
    </div>
  );
}

/**
 * The footer "Terms of Use" link (TU-F08). A person can read the live policy at any time — before or
 * after accepting — without leaving the page. Opens a dialog rendering the same current policy; it is
 * read-only (no accept control), since the footer is not the gate.
 */
export function TermsFooterLink() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="tb-footer__terms-link" onClick={() => setOpen(true)}>
        Terms of Use
      </button>
      {open ? <TermsDialog onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/** The read-only policy dialog behind the footer link. */
function TermsDialog({ onClose }: { onClose: () => void }) {
  const load = useCurrentPolicy();
  const headingId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // TU-F07 / PC-NFR01 modal-dialog affordances (WCAG AA):
  //   - focus moves into the dialog on open (the close button), and is RESTORED to whatever opened it
  //     (the footer link) on close, so keyboard focus never lands nowhere;
  //   - Escape closes;
  //   - because aria-modal="true" PROMISES the rest of the page is inert, Tab/Shift+Tab are trapped
  //     inside the panel — otherwise a keyboard user tabs out into a background they're told is hidden.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      // Wrap at the ends so focus stays within the panel.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // Restore focus to the opener so the keyboard user resumes where they were (WCAG 2.4.3).
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <div
      className="tb-terms tb-terms--overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
    >
      <div className="tb-terms__backdrop" onClick={onClose} aria-hidden="true" />
      <div className="tb-terms__panel tb-terms__panel--dialog" role="document" ref={panelRef}>
        <h1 id={headingId} className="tb-terms__title">
          Terms of Use
        </h1>
        {load.status === 'ready' ? (
          <>
            <p className="tb-terms__meta">
              Version {load.policy.versionLabel} · Effective {formatDate(load.policy.effectiveDate)}
            </p>
            <PolicyReader policy={load.policy} scrollLabelledBy={headingId} />
          </>
        ) : load.status === 'loading' ? (
          <p className="tb-terms__status">Loading…</p>
        ) : (
          <p className="tb-terms__status">The Terms of Use could not be loaded right now.</p>
        )}
        <div className="tb-terms__actions">
          <button type="button" className="tb-terms__accept" onClick={onClose} ref={closeRef}>
            Close
          </button>
        </div>
      </div>
      <TermsGateStyles />
    </div>
  );
}

/** Format an ISO date (YYYY-MM-DD) as a readable date, defensively (falls back to the raw string). */
function formatDate(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

/**
 * The gate's styles, on the frame's --tb-* tokens. Inlined (CSP forbids external sheets) and scoped to
 * .tb-terms*. Mounted once alongside whichever surface is showing; a duplicate <style> is harmless.
 */
function TermsGateStyles() {
  return (
    <style>{`
.tb-terms { display: flex; align-items: flex-start; justify-content: center; padding: 1.5rem 1rem 3rem; }
.tb-terms--overlay { position: fixed; inset: 0; z-index: 200; align-items: center; padding: 2rem 1rem; overflow: auto; }
.tb-terms__backdrop { position: fixed; inset: 0; background: rgba(20, 22, 30, 0.55); }
.tb-terms__panel {
  position: relative; z-index: 1; width: 100%; max-width: 720px; background: var(--tb-bg, #fff);
  color: var(--tb-ink, #1a1c23); border: 1px solid var(--tb-line, #d7d9e0); border-radius: 12px;
  padding: 1.5rem 1.5rem 1.25rem; box-shadow: 0 8px 30px rgba(20,22,30,0.10);
  font-family: system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
}
.tb-terms__panel--dialog { max-height: calc(100vh - 4rem); display: flex; flex-direction: column; }
.tb-terms__title { font-size: 1.4rem; margin: 0 0 0.25rem; }
.tb-terms__meta { color: var(--tb-muted, #55596b); font-size: 0.9rem; margin: 0 0 0.75rem; }
.tb-terms__lede { margin: 0 0 1rem; }
.tb-terms__scroll {
  border: 1px solid var(--tb-line, #d7d9e0); border-radius: 8px; background: var(--tb-surface, #f4f5f8);
  padding: 1rem 1.1rem; max-height: 46vh; overflow-y: auto; overscroll-behavior: contain;
}
.tb-terms__panel--dialog .tb-terms__scroll { max-height: none; flex: 1 1 auto; min-height: 0; }
.tb-terms__scroll:focus-visible { outline: 2px solid var(--tb-accent, #244b8f); outline-offset: 2px; }
/* WCAG 2.4.7: a visible focus indicator on every interactive control in the gate, not colour alone. */
.tb-terms__title:focus-visible { outline: 2px solid var(--tb-accent, #244b8f); outline-offset: 4px; border-radius: 4px; }
.tb-terms__accept:focus-visible, .tb-terms__decline:focus-visible, .tb-footer__terms-link:focus-visible {
  outline: 2px solid var(--tb-accent, #244b8f); outline-offset: 2px;
}
.tb-terms__whatchanged {
  background: #fff4d6; color: #5a4300; border: 1px solid #e6cf8f; border-radius: 6px;
  padding: 0.6rem 0.75rem; margin: 0 0 0.9rem; font-size: 0.95rem;
}
.tb-terms__body h2 { font-size: 1.15rem; margin: 0 0 0.5rem; }
.tb-terms__body h3 { font-size: 1rem; margin: 1rem 0 0.35rem; }
.tb-terms__body p { margin: 0 0 0.7rem; line-height: 1.55; }
.tb-terms__body p:last-child { margin-bottom: 0; }
.tb-terms__hint { margin: 0.85rem 0 0.5rem; color: var(--tb-muted, #55596b); font-size: 0.9rem; }
.tb-terms__error { margin: 0.5rem 0; color: #8a1c1c; }
.tb-terms__actions { display: flex; gap: 0.75rem; margin-top: 0.75rem; flex-wrap: wrap; }
.tb-terms__accept {
  background: var(--tb-accent, #244b8f); color: var(--tb-accent-ink, #fff); border: 1px solid var(--tb-accent, #244b8f);
  border-radius: 8px; padding: 0.6rem 1.1rem; font-size: 1rem; cursor: pointer;
}
.tb-terms__accept:disabled { opacity: 0.5; cursor: not-allowed; }
.tb-terms__decline {
  background: transparent; color: var(--tb-ink, #1a1c23); border: 1px solid var(--tb-line, #d7d9e0);
  border-radius: 8px; padding: 0.6rem 1.1rem; font-size: 1rem; cursor: pointer;
}
.tb-terms__status { color: var(--tb-muted, #55596b); }
.tb-footer__terms-link {
  background: none; border: none; padding: 0; margin: 0.25rem 0 0; color: var(--tb-accent, #244b8f);
  text-decoration: underline; cursor: pointer; font: inherit;
}
    `}</style>
  );
}
